/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type {
  CartItem,
  CustomerInfo,
  Coupon,
  Order,
  Product,
  SpecialOffer,
} from '@/types';
import { ClientService, type ClientCreatePayload } from '@/services/domain/ClientService';
import { OrderService } from '@/services/domain/OrderService';
import { waService } from '@/services/domain/WhatsAppService';
import { AuthService } from '@/services/domain/AuthService';
import { ProductService } from '@/services/domain/ProductService';
import { OfferService } from '@/services/domain/OfferService';
import { CouponService } from '@/services/domain/CouponService';
import { CategoryService } from '@/services/domain/CategoryService';
import type {
  OrderInput,
  MeResponse,
  Product as ApiProduct,
  Offer as ApiOffer,
  Coupon as ApiCoupon,
  Category as ApiCategory,
  CategoryResponse,
  CategoryInput,
  Client,
  OrderAdminResponse,
  Pagination,
} from '@/types/api';

export interface AppContextType {
  cart: CartItem[];
  setCart: React.Dispatch<React.SetStateAction<CartItem[]>>;
  isCartLoaded: boolean;
  setIsCartLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  isCartOpen: boolean;
  setIsCartOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isCheckoutOpen: boolean;
  setIsCheckoutOpen: React.Dispatch<React.SetStateAction<boolean>>;
  orders: Order[];
  setOrders: React.Dispatch<React.SetStateAction<Order[]>>;
  customerInfo: CustomerInfo;
  setCustomerInfo: React.Dispatch<React.SetStateAction<CustomerInfo>>;
  isAdminAuthenticated: boolean;
  setIsAdminAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
  adminUser: MeResponse | null;
  adminToken: string | null;
  adminProducts: Product[];
  setAdminProducts: React.Dispatch<React.SetStateAction<Product[]>>;
  products: Product[];
  specialOffers: SpecialOffer[];
  setSpecialOffers: React.Dispatch<React.SetStateAction<SpecialOffer[]>>;
  coupons: Coupon[];
  setCoupons: React.Dispatch<React.SetStateAction<Coupon[]>>;
  appliedCoupon: Coupon | null;
  setAppliedCoupon: React.Dispatch<React.SetStateAction<Coupon | null>>;
  discountCode: string;
  setDiscountCode: React.Dispatch<React.SetStateAction<string>>;
  selectedCategory: string;
  setSelectedCategory: React.Dispatch<React.SetStateAction<string>>;
  isCatshopMenuOpen: boolean;
  setIsCatshopMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  productCategories: ApiCategory[];
  addToCart: (product: Product) => void;
  removeFromCart: (productId: number) => void;
  updateQuantity: (productId: number, quantity: number) => void;
  getCartTotal: () => number;
  getCartItemsCount: () => number;
  finishOrder: (event: React.MouseEvent) => Promise<void>;
  applyDiscountCode: (code: string) => Promise<boolean>;
  removeDiscountCode: () => void;
  calculateCartTotalWithDiscount: (total: number) => number;
  getDiscountAmount: (total: number) => number;
  adminLogin: (email: string, password: string) => Promise<boolean>;
  adminLogout: () => Promise<void>;
  addProduct: (data: {
    name: string;
    description?: string;
    imageUrl?: string;
    imageFile?: File;
    price: number;
    categoryId: number;
    stockQuantity?: number;
    isActive?: boolean;
    isAvailable?: boolean;
  }) => Promise<void>;
  updateProduct: (productId: number, data: {
    name?: string;
    description?: string;
    imageUrl?: string;
    imageFile?: File;
    price?: number;
    categoryId?: number;
    stockQuantity?: number;
    isActive?: boolean;
    isAvailable?: boolean;
  }) => Promise<void>;
  deleteProduct: (productId: number) => Promise<void>;
  toggleProductStock: (productId: number) => Promise<void>;
  addSpecialOffer: (data: {
    productId: number;
    discountPercent: number;
    startsAt?: string | null;
    endsAt?: string | null;
  }) => Promise<void>;
  updateSpecialOffer: (offerId: number, data: {
    productId?: number;
    discountPercent?: number;
    startsAt?: string | null;
    endsAt?: string | null;
    isActive?: boolean;
  }) => Promise<void>;
  deleteSpecialOffer: (offerId: number) => Promise<void>;
  addCoupon: (data: {
    code: string;
    percentage: number;
    description?: string;
    minValue?: number;
    maxUses?: number | null;
    expiryDate?: string;
    isActive?: boolean;
  }) => Promise<void>;
  updateCoupon: (couponId: number, data: {
    code?: string;
    percentage?: number;
    description?: string;
    minValue?: number;
    maxUses?: number | null;
    expiryDate?: string;
    isActive?: boolean;
  }) => Promise<void>;
  deleteCoupon: (couponId: number) => Promise<void>;
  adminCategories: CategoryResponse[];
  loadAdminCategories: (activeOnly?: boolean) => Promise<void>;
  createCategory: (data: CategoryInput) => Promise<void>;
  updateCategory: (id: number, data: CategoryInput) => Promise<void>;
  toggleCategoryStatus: (id: number) => Promise<void>;
  deleteCategory: (id: number) => Promise<void>;
  adminOrders: Pagination<OrderAdminResponse> | null;
  loadAdminOrders: (params?: { page?: number; per_page?: number; status?: string }) => Promise<Pagination<OrderAdminResponse>>;
  confirmOrder: (id: number) => Promise<void>;
  cancelOrder: (id: number) => Promise<void>;
  updateOrderStatus: (id: number, status: string) => Promise<void>;
  adminClients: Client[];
  loadAdminClients: () => Promise<Client[]>;
  updateClient: (id: number, data: Partial<Client>) => Promise<void>;
  deleteClient: (id: number) => Promise<void>;
  parsePrice: (price: string) => number;
  getActiveOffers: () => SpecialOffer[];
  getProductDiscount: (productId: number) => number;
  calculateDiscountedPrice: (price: string, discount: number) => string;
  whatsappNumber: string;
  openWhatsApp: (message?: string, number?: string | null) => string;
}

export const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
}

const ADMIN_TOKEN_STORAGE_KEY = 'admin_token';

const createEmptyCustomerInfo = (): CustomerInfo => ({
  name: '',
  phone: '',
  email: '',
  address: '',
  city: '',
  zipCode: '',
  state: '',
  notes: '',
});

const DEFAULT_WHATSAPP_NUMBER = waService.getDefaultNumber();

const formatCurrencyBRL = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const mapApiProductToContext = (apiProduct: ApiProduct, categoriesById: Record<number, string>): Product => {
  const priceCents = apiProduct.price_cents ?? Math.round((apiProduct.price_reais ?? 0) * 100);
  const price = priceCents / 100;
  const originalCents = apiProduct.original_price_cents ?? priceCents;
  const hasDiscount = originalCents > priceCents;
  const discountPercent = hasDiscount
    ? Math.round(((originalCents - priceCents) / originalCents) * 100)
    : undefined;
  const categoryName = apiProduct.category_id ? categoriesById[apiProduct.category_id] ?? 'Outros' : 'Outros';

  return {
    id: apiProduct.id,
    name: apiProduct.name,
    description: apiProduct.description ?? '',
    image: '🐾',
    imageUrl: apiProduct.image_url ?? undefined,
    price: apiProduct.price_formatted ?? formatCurrencyBRL(price),
    category: categoryName,
    categoryId: apiProduct.category_id ?? undefined,
    priceNumber: apiProduct.price_reais ?? price,
    inStock: apiProduct.is_available ?? (apiProduct.stock_quantity ?? 0) > 0,
    stockQuantity: apiProduct.stock_quantity ?? undefined,
    isActive: apiProduct.is_active ?? true,
    discount: discountPercent,
    isSpecialOffer: apiProduct.offer_applied ?? false,
    originalPrice: hasDiscount ? originalCents / 100 : undefined,
    appliedDiscount: discountPercent,
  };
};

const mapApiOfferToContext = (
  offer: ApiOffer,
  productLookup: Map<number, Product>,
): SpecialOffer => {
  const relatedProduct = productLookup.get(offer.product_id);
  return {
    id: offer.id,
    productId: offer.product_id,
    title: relatedProduct ? `Oferta em ${relatedProduct.name}` : `Oferta #${offer.id}`,
    description: relatedProduct?.description ?? '',
    discountPercentage: offer.discount_percent,
    startDate: offer.starts_at ?? '',
    endDate: offer.ends_at ?? '',
    isActive: offer.is_active ?? true,
  };
};

const mapApiCouponToContext = (coupon: ApiCoupon): Coupon => {
  const isPercent = coupon.type === 'percent';
  const percentage = isPercent ? coupon.value : 0;
  const valueDescription = isPercent
    ? `${coupon.value}% OFF`
    : formatCurrencyBRL((coupon.value ?? 0) / 100);

  const metadataDescription = coupon.metadata && typeof coupon.metadata === 'object'
    ? String((coupon.metadata as Record<string, unknown>).description ?? '')
    : '';

  return {
    id: coupon.id,
    code: coupon.code,
    percentage,
    description: metadataDescription || `Cupom ${coupon.code} (${valueDescription})`,
    minValue: coupon.min_order_cents != null ? coupon.min_order_cents / 100 : undefined,
    maxUses: coupon.usage_limit ?? null,
    currentUses: coupon.usage_count ?? null,
    isActive: coupon.is_active ?? true,
    expiryDate: coupon.expires_at ?? null,
    calculatedAmountCents: 0,
  };
};

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [adminProducts, setAdminProducts] = useState<Product[]>([]);
  const [adminCategories, setAdminCategories] = useState<CategoryResponse[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [adminOrders, setAdminOrders] = useState<Pagination<OrderAdminResponse> | null>(null);
  const [specialOffers, setSpecialOffers] = useState<SpecialOffer[]>([]);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [adminClients, setAdminClients] = useState<Client[]>([]);
  const [appliedCoupon, setAppliedCoupon] = useState<Coupon | null>(null);
  const [discountCode, setDiscountCode] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [isCartLoaded, setIsCartLoaded] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isCatshopMenuOpen, setIsCatshopMenuOpen] = useState(false);
  const [productCategories, setProductCategories] = useState<ApiCategory[]>([]);
  const [adminToken, setAdminToken] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
  });
  const lastOrderParamsRef = useRef<{ page?: number; per_page?: number; status?: string } | undefined>(undefined);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return Boolean(localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY));
  });
  const [adminUser, setAdminUser] = useState<MeResponse | null>(null);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>(() => createEmptyCustomerInfo());

  const whatsappNumber = DEFAULT_WHATSAPP_NUMBER;

  const openWhatsApp = useCallback(
    (message?: string, number?: string | null) => waService.openChat({ number, message }),
    [],
  );

  const emptyOffersPagination: Pagination<ApiOffer> = {
    data: [],
    total: 0,
    per_page: 0,
    current_page: 1,
    last_page: 1,
  };

  useEffect(() => {
    if (adminToken) {
      AuthService.setToken(adminToken);
      localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, adminToken);
    } else {
      AuthService.setToken(null);
      localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
    }
  }, [adminToken]);

  useEffect(() => {
    if (!adminToken) {
      setAdminUser(null);
      setIsAdminAuthenticated(false);
      return;
    }

    let isMounted = true;

    const fetchProfile = async () => {
      try {
        const profile = await AuthService.me();
        if (!isMounted) return;
        setAdminUser(profile);
        setIsAdminAuthenticated(true);
      } catch (error) {
        console.error('Erro ao validar sessão do administrador', error);
        if (!isMounted) return;
        setAdminToken(null);
        setAdminUser(null);
        setIsAdminAuthenticated(false);
      }
    };

    void fetchProfile();

    return () => {
      isMounted = false;
    };
  }, [adminToken]);

  useEffect(() => {
    let isMounted = true;

    const loadInitialData = async () => {
      try {
        setIsCartLoaded(false);

        const [categoriesResponse, productResponse, offersResponse, couponsResponse] = await Promise.all([
          ProductService.categories().catch(() => [] as ApiCategory[]),
          ProductService.list({ per_page: 100 }),
          OfferService.list({ per_page: 100 }).catch(() => emptyOffersPagination),
          CouponService.admin.list().catch(() => [] as ApiCoupon[]),
        ]);

        if (!isMounted) return;

        setProductCategories(categoriesResponse);
        const categoriesLookup = categoriesResponse.reduce<Record<number, string>>((lookup, category) => {
          lookup[category.id] = category.name;
          return lookup;
        }, {});

        const mappedProducts = productResponse.data.map(product => mapApiProductToContext(product, categoriesLookup));
        const productsLookup = new Map(mappedProducts.map(product => [product.id, product]));
        setProducts(mappedProducts);
        setAdminProducts(mappedProducts);

        const offersArray = Array.isArray((offersResponse as Pagination<ApiOffer>).data)
          ? (offersResponse as Pagination<ApiOffer>).data
          : Array.isArray(offersResponse)
            ? offersResponse
            : [];

        setSpecialOffers(offersArray.map(offer => mapApiOfferToContext(offer, productsLookup)));

        if (Array.isArray(couponsResponse)) {
          setCoupons(couponsResponse.map(mapApiCouponToContext));
        }

        const storedCart = localStorage.getItem('cart');
        const storedOrders = localStorage.getItem('orders');
        const storedCategory = localStorage.getItem('selectedCategory');

        if (storedCart) setCart(JSON.parse(storedCart));
        if (storedOrders) setOrders(JSON.parse(storedOrders));
        if (storedCategory) setSelectedCategory(storedCategory);
      } catch (error) {
        console.error('Erro ao carregar dados iniciais', error);
      } finally {
        if (isMounted) setIsCartLoaded(true);
      }
    };

    void loadInitialData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isCartLoaded) return;
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart, isCartLoaded]);

  useEffect(() => {
    localStorage.setItem('orders', JSON.stringify(orders));
  }, [orders]);

  useEffect(() => {
    localStorage.setItem('selectedCategory', selectedCategory);
  }, [selectedCategory]);

  // Special offers e descontos passam a ser gerenciados pelo backend; não persistir duplicado

  const parsePrice = useCallback((price: string) => {
    return parseFloat(price.replace('R$ ', '').replace('.', '').replace(',', '.'));
  }, []);

  const calculateDiscountedPrice = useCallback(
    (price: string, discountPercentage: number) => {
      const numeric = parsePrice(price);
      const discounted = numeric * (1 - discountPercentage / 100);
      return `R$ ${discounted.toFixed(2).replace('.', ',')}`;
    },
    [parsePrice],
  );

  const getActiveOffers = useCallback(() => {
    const now = new Date();
    return specialOffers.filter(offer => {
      const start = new Date(offer.startDate);
      const end = new Date(offer.endDate);
      return offer.isActive && start <= now && end >= now;
    });
  }, [specialOffers]);

  const getProductDiscount = useCallback(
    (productId: number) => {
      const activeOffer = getActiveOffers().find(offer => offer.productId === productId);
      return activeOffer ? activeOffer.discountPercentage : 0;
    },
    [getActiveOffers],
  );

  const addToCart = useCallback((product: Product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item,
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  }, []);

  const removeFromCart = useCallback((productId: number) => {
    setCart(prev => prev.filter(item => item.id !== productId));
  }, []);

  const updateQuantity = useCallback((productId: number, quantity: number) => {
    setCart(prev => prev.map(item => (item.id === productId ? { ...item, quantity } : item)));
  }, []);

  const getCartTotal = useCallback(() => {
    return cart.reduce((total, item) => total + parsePrice(item.price) * item.quantity, 0);
  }, [cart, parsePrice]);

  const getCartItemsCount = useCallback(() => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  }, [cart]);

  const calculateCartTotalWithDiscount = useCallback(
    (total: number) => {
      if (!appliedCoupon) return total;
      if (appliedCoupon.calculatedAmountCents != null) {
        return Math.max(total - appliedCoupon.calculatedAmountCents / 100, 0);
      }
      if (appliedCoupon.percentage) {
        return total * (1 - appliedCoupon.percentage / 100);
      }
      return total;
    },
    [appliedCoupon],
  );

  const getDiscountAmount = useCallback(
    (total: number) => {
      if (!appliedCoupon) return 0;
      if (appliedCoupon.calculatedAmountCents != null) {
        return appliedCoupon.calculatedAmountCents / 100;
      }
      if (appliedCoupon.percentage) {
        return total * (appliedCoupon.percentage / 100);
      }
      return 0;
    },
    [appliedCoupon],
  );

  const applyDiscountCode = useCallback(
    async (code: string) => {
      try {
        const cartTotalCents = Math.round(getCartTotal() * 100);
        const response = await CouponService.validate(code, cartTotalCents);

        if (!response.success) {
          setAppliedCoupon(null);
          setDiscountCode('');
          return false;
        }

        const mappedCoupon = mapApiCouponToContext(response.data.discount);
        mappedCoupon.calculatedAmountCents = response.data.discount_amount_cents;

        setAppliedCoupon(mappedCoupon);
        setDiscountCode(code.toUpperCase());
        return true;
      } catch (error) {
        console.error('Erro ao validar cupom', error);
        setAppliedCoupon(null);
        setDiscountCode('');
        return false;
      }
    },
    [getCartTotal],
  );

  const removeDiscountCode = useCallback(() => {
    setAppliedCoupon(null);
    setDiscountCode('');
  }, []);

  const finishOrder = useCallback(
    async (event: React.MouseEvent) => {
      event.preventDefault();
      if (cart.length === 0) return;

      try {
        const clientPayload: ClientCreatePayload = {
          name: customerInfo.name,
          phone: customerInfo.phone,
          email: customerInfo.email || null,
          street: customerInfo.address,
          city: customerInfo.city,
          state: customerInfo.state || null,
          zip_code: customerInfo.zipCode,
          notes: customerInfo.notes || null,
        };

        const client = await ClientService.create(clientPayload);

        const orderInput: OrderInput = {
          client_id: client.id,
          items: cart.map(item => ({ product_id: item.id, quantity: item.quantity })),
        };

        const orderResponse = await OrderService.create(orderInput);

        const total = getCartTotal();
        const totalWithDiscount = calculateCartTotalWithDiscount(total);

        const order: Order = {
          id: orderResponse.id.toString(),
          items: [...cart],
          total: totalWithDiscount,
          customerInfo: { ...customerInfo },
          status: (orderResponse.status as Order['status']) ?? 'pending',
          date: orderResponse.created_at ?? new Date().toISOString(),
        };

        setOrders(prev => [...prev, order]);
        setCart([]);
        setIsCheckoutOpen(false);
        setCustomerInfo(createEmptyCustomerInfo());
        removeDiscountCode();

        if (orderResponse.whatsapp_url) {
          window.open(orderResponse.whatsapp_url, '_blank');
        } else {
          const itemsSummary = cart
            .map(item => `${item.name} x${item.quantity}`)
            .join(', ');
          const summaryLines = [
            'Novo pedido:',
            `Cliente: ${customerInfo.name}`,
            `Itens: ${itemsSummary}`,
            `Total: R$ ${totalWithDiscount.toFixed(2)}`,
          ];
          openWhatsApp(summaryLines.join('\n'));
        }
      } catch (error) {
        console.error('Erro ao finalizar pedido', error);
        alert('Não foi possível concluir o pedido. Tente novamente.');
      }
    },
    [
      cart,
      customerInfo,
      calculateCartTotalWithDiscount,
      getCartTotal,
      removeDiscountCode,
      openWhatsApp,
    ],
  );

  const refreshProducts = useCallback(async () => {
    try {
      const [categoriesResponse, productResponse] = await Promise.all([
        ProductService.categories().catch(() => productCategories),
        ProductService.list({ per_page: 100 }),
      ]);

      const categoriesLookup = categoriesResponse.reduce<Record<number, string>>((lookup, category) => {
        lookup[category.id] = category.name;
        return lookup;
      }, {});

      const mappedProducts = productResponse.data.map(product => mapApiProductToContext(product, categoriesLookup));
      const productsLookup = new Map(mappedProducts.map(product => [product.id, product]));

      setProductCategories(categoriesResponse);
      setProducts(mappedProducts);
      setAdminProducts(mappedProducts);

      try {
        const offers = await OfferService.list({ per_page: 100 });
        const offersData = Array.isArray(offers.data) ? offers.data : emptyOffersPagination.data;
        setSpecialOffers(offersData.map(offer => mapApiOfferToContext(offer, productsLookup)));
      } catch (error) {
        console.error('Erro ao recarregar ofertas', error);
      }
    } catch (error) {
      console.error('Erro ao recarregar produtos', error);
    }
  }, [productCategories]);

  const adminLogin = useCallback(async (email: string, password: string) => {
    try {
      const { token } = await AuthService.login(email, password);
      AuthService.setToken(token);
      setAdminToken(token);
      const profile = await AuthService.me();
      setAdminUser(profile);
      setIsAdminAuthenticated(true);
      return true;
    } catch (error) {
      console.error('Erro ao autenticar administrador', error);
      setAdminToken(null);
      setAdminUser(null);
      setIsAdminAuthenticated(false);
      return false;
    }
  }, []);

  const adminLogout = useCallback(async () => {
    try {
      await AuthService.logout();
    } catch (error) {
      console.error('Erro ao encerrar sessão do administrador', error);
    } finally {
      setAdminToken(null);
      setAdminUser(null);
      setIsAdminAuthenticated(false);
    }
  }, []);

  const addProduct = useCallback(async (data: {
    name: string;
    description?: string;
    imageUrl?: string;
    imageFile?: File;
    price: number;
    categoryId: number;
    stockQuantity?: number;
    isActive?: boolean;
    isAvailable?: boolean;
  }) => {
    if (data.imageFile) {
      const formData = new FormData();
      formData.append('name', data.name);
      if (data.description) formData.append('description', data.description);
      formData.append('price_cents', String(Math.round(data.price * 100)));
      formData.append('category_id', String(data.categoryId));
      if (data.stockQuantity !== undefined) {
        formData.append('stock_quantity', String(data.stockQuantity));
      }
      if (data.isActive !== undefined) {
        formData.append('is_active', data.isActive ? '1' : '0');
      }
      if (data.isAvailable !== undefined) {
        formData.append('is_available', data.isAvailable ? '1' : '0');
      }
      formData.append('image', data.imageFile);

      await ProductService.admin.create(formData as any);
    } else {
      const payload: Partial<ApiProduct> & { name: string; price_cents: number } = {
        name: data.name,
        description: data.description,
        image_url: data.imageUrl,
        price_cents: Math.round(data.price * 100),
        category_id: data.categoryId,
        stock_quantity: data.stockQuantity,
        is_active: data.isActive,
        is_available: data.isAvailable,
      };

      await ProductService.admin.create(payload);
    }

    await refreshProducts();
  }, [refreshProducts]);

  const loadAdminCategories = useCallback(async (activeOnly = false) => {
    const categories = await CategoryService.admin.list(activeOnly);
    setAdminCategories(categories);
  }, []);

  const createCategory = useCallback(async (data: CategoryInput) => {
    const created = await CategoryService.admin.create(data);
    setAdminCategories(prev => [...prev, created]);
    await refreshProducts();
  }, [refreshProducts]);

  const updateCategory = useCallback(async (id: number, data: CategoryInput) => {
    const updated = await CategoryService.admin.update(id, data);
    setAdminCategories(prev => prev.map(category => (category.id === id ? updated : category)));
    await refreshProducts();
  }, [refreshProducts]);

  const toggleCategoryStatus = useCallback(async (id: number) => {
    const updated = await CategoryService.admin.toggleActive(id);
    setAdminCategories(prev => prev.map(category => (category.id === id ? updated : category)));
    await refreshProducts();
  }, [refreshProducts]);

  const deleteCategory = useCallback(async (id: number) => {
    await CategoryService.admin.delete(id);
    setAdminCategories(prev => prev.filter(category => category.id !== id));
    await refreshProducts();
  }, [refreshProducts]);

  const loadAdminOrders = useCallback(async (params?: { page?: number; per_page?: number; status?: string }) => {
    lastOrderParamsRef.current = params;
    const ordersPagination = await OrderService.admin.list(params);
    setAdminOrders(ordersPagination);
    return ordersPagination;
  }, []);

  const confirmOrder = useCallback(async (id: number) => {
    await OrderService.admin.confirm(id);
    await loadAdminOrders(lastOrderParamsRef.current);
  }, [loadAdminOrders]);

  const cancelOrder = useCallback(async (id: number) => {
    await OrderService.admin.cancel(id);
    await loadAdminOrders(lastOrderParamsRef.current);
  }, [loadAdminOrders]);

  const updateOrderStatus = useCallback(async (id: number, status: string) => {
    await OrderService.admin.updateStatus(id, status);
    await loadAdminOrders(lastOrderParamsRef.current);
  }, [loadAdminOrders]);

  const loadAdminClients = useCallback(async () => {
    const clients = await ClientService.admin.list();
    setAdminClients(clients);
    return clients;
  }, []);

  const updateClient = useCallback(async (id: number, data: Partial<Client>) => {
    const updated = await ClientService.admin.update(id, data);
    setAdminClients(prev => prev.map(client => (client.id === id ? updated : client)));
  }, []);

  const deleteClient = useCallback(async (id: number) => {
    await ClientService.admin.delete(id);
    setAdminClients(prev => prev.filter(client => client.id !== id));
  }, []);

  useEffect(() => {
    if (!isAdminAuthenticated) {
      setAdminOrders(null);
      setAdminClients([]);
      return;
    }

    void loadAdminCategories();
    void loadAdminOrders(lastOrderParamsRef.current);
    void loadAdminClients();
  }, [isAdminAuthenticated, loadAdminCategories, loadAdminOrders, loadAdminClients]);

  const updateProduct = useCallback(async (
    productId: number,
    data: {
      name?: string;
      description?: string;
      imageUrl?: string;
      imageFile?: File;
      price?: number;
      categoryId?: number;
      stockQuantity?: number;
      isActive?: boolean;
      isAvailable?: boolean;
    },
  ) => {
    if (data.imageFile) {
      const formData = new FormData();
      if (data.name !== undefined) formData.append('name', data.name);
      if (data.description !== undefined) formData.append('description', data.description);
      if (data.price !== undefined) {
        formData.append('price_cents', String(Math.round(data.price * 100)));
      }
      if (data.categoryId !== undefined) {
        formData.append('category_id', String(data.categoryId));
      }
      if (data.stockQuantity !== undefined) {
        formData.append('stock_quantity', String(data.stockQuantity));
      }
      if (data.isActive !== undefined) {
        formData.append('is_active', data.isActive ? '1' : '0');
      }
      if (data.isAvailable !== undefined) {
        formData.append('is_available', data.isAvailable ? '1' : '0');
      }
      formData.append('image', data.imageFile);

      await ProductService.admin.update(productId, formData as any);
    } else {
      const payload: Partial<ApiProduct> = {
        name: data.name,
        description: data.description,
        image_url: data.imageUrl,
        price_cents: data.price !== undefined ? Math.round(data.price * 100) : undefined,
        category_id: data.categoryId,
        stock_quantity: data.stockQuantity,
        is_active: data.isActive,
        is_available: data.isAvailable,
      };

      await ProductService.admin.update(productId, payload);
    }

    await refreshProducts();
  }, [refreshProducts]);

  const deleteProduct = useCallback(async (productId: number) => {
    await ProductService.admin.delete(productId);
    await refreshProducts();
  }, [refreshProducts]);

  const toggleProductStock = useCallback(async (productId: number) => {
    await ProductService.admin.toggleActive(productId);
    await refreshProducts();
  }, [refreshProducts]);

  const addSpecialOffer = useCallback(async (data: {
    productId: number;
    discountPercent: number;
    startsAt?: string | null;
    endsAt?: string | null;
  }) => {
    const payload = {
      product_id: data.productId,
      discount_percent: data.discountPercent,
      starts_at: data.startsAt,
      ends_at: data.endsAt,
    };

    const created = await OfferService.create(payload);
    setSpecialOffers(prev => [...prev, mapApiOfferToContext(created, new Map(adminProducts.map(product => [product.id, product])))]);
  }, [adminProducts]);

  const updateSpecialOffer = useCallback(async (
    offerId: number,
    data: {
      productId?: number;
      discountPercent?: number;
      startsAt?: string | null;
      endsAt?: string | null;
      isActive?: boolean;
    },
  ) => {
    const payload: Partial<ApiOffer> = {
      product_id: data.productId,
      discount_percent: data.discountPercent,
      starts_at: data.startsAt,
      ends_at: data.endsAt,
      is_active: data.isActive,
    };

    const updated = await OfferService.update(offerId, payload);
    setSpecialOffers(prev => prev.map(offer => (
      offer.id === offerId
        ? mapApiOfferToContext(updated, new Map(adminProducts.map(product => [product.id, product])))
        : offer
    )));
  }, [adminProducts]);

  const deleteSpecialOffer = useCallback(async (offerId: number) => {
    await OfferService.delete(offerId);
    setSpecialOffers(prev => prev.filter(offer => offer.id !== offerId));
  }, []);

  const addCoupon = useCallback(async (data: {
    code: string;
    percentage: number;
    description?: string;
    minValue?: number;
    maxUses?: number | null;
    expiryDate?: string;
    isActive?: boolean;
  }) => {
    const payload: Partial<ApiCoupon> = {
      code: data.code,
      value: data.percentage,
      min_order_cents: data.minValue !== undefined ? Math.round(data.minValue * 100) : undefined,
      usage_limit: data.maxUses ?? undefined,
      expires_at: data.expiryDate ?? undefined,
      is_active: data.isActive,
      metadata: data.description ? { description: data.description } : undefined,
    };

    const created = await CouponService.admin.create({
      code: payload.code!,
      type: 'percent',
      value: data.percentage,
      min_order_cents: payload.min_order_cents,
      usage_limit: payload.usage_limit,
      expires_at: payload.expires_at,
      is_active: payload.is_active,
      metadata: payload.metadata,
    });

    setCoupons(prev => [mapApiCouponToContext(created), ...prev]);
  }, []);

  const updateCoupon = useCallback(async (
    couponId: number,
    data: {
      code?: string;
      percentage?: number;
      description?: string;
      minValue?: number;
      maxUses?: number | null;
      expiryDate?: string;
      isActive?: boolean;
    },
  ) => {
    const payload: Partial<ApiCoupon> = {
      code: data.code,
      value: data.percentage,
      min_order_cents: data.minValue !== undefined ? Math.round(data.minValue * 100) : undefined,
      usage_limit: data.maxUses ?? undefined,
      expires_at: data.expiryDate ?? undefined,
      is_active: data.isActive,
      metadata: data.description ? { description: data.description } : undefined,
    };

    const updated = await CouponService.admin.update(couponId, payload);
    setCoupons(prev => prev.map(coupon =>
      coupon.id === couponId ? mapApiCouponToContext(updated) : coupon,
    ));
  }, []);

  const deleteCoupon = useCallback(async (couponId: number) => {
    await CouponService.admin.delete(couponId);
    setCoupons(prev => prev.filter(coupon => coupon.id !== couponId));
  }, []);

  const contextValue = useMemo<AppContextType>(() => ({
    cart,
    setCart,
    isCartLoaded,
    setIsCartLoaded,
    isCartOpen,
    setIsCartOpen,
    isCheckoutOpen,
    setIsCheckoutOpen,
    orders,
    setOrders,
    customerInfo,
    setCustomerInfo,
    isAdminAuthenticated,
    setIsAdminAuthenticated,
    adminUser,
    adminToken,
    adminProducts,
    setAdminProducts,
    products,
    productCategories,
    specialOffers,
    setSpecialOffers,
    coupons,
    setCoupons,
    appliedCoupon,
    setAppliedCoupon,
    discountCode,
    setDiscountCode,
    selectedCategory,
    setSelectedCategory,
    isCatshopMenuOpen,
    setIsCatshopMenuOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    getCartTotal,
    getCartItemsCount,
    finishOrder,
    applyDiscountCode,
    removeDiscountCode,
    calculateCartTotalWithDiscount,
    getDiscountAmount,
    adminLogin,
    adminLogout,
    addProduct,
    updateProduct,
    deleteProduct,
    toggleProductStock,
    addSpecialOffer,
    updateSpecialOffer,
    deleteSpecialOffer,
    addCoupon,
    updateCoupon,
    deleteCoupon,
    adminCategories,
    loadAdminCategories,
    createCategory,
    updateCategory,
    toggleCategoryStatus,
    deleteCategory,
    adminOrders,
    loadAdminOrders,
    confirmOrder,
    cancelOrder,
    updateOrderStatus,
    adminClients,
    loadAdminClients,
    updateClient,
    deleteClient,
    parsePrice,
    getActiveOffers,
    getProductDiscount,
    calculateDiscountedPrice,
    whatsappNumber,
    openWhatsApp,
  }), [
    cart,
    isCartLoaded,
    isCartOpen,
    isCheckoutOpen,
    orders,
    customerInfo,
    isAdminAuthenticated,
    adminProducts,
    products,
    productCategories,
    specialOffers,
    coupons,
    appliedCoupon,
    discountCode,
    selectedCategory,
    isCatshopMenuOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    getCartTotal,
    getCartItemsCount,
    finishOrder,
    applyDiscountCode,
    removeDiscountCode,
    calculateCartTotalWithDiscount,
    getDiscountAmount,
    adminLogin,
    adminLogout,
    addProduct,
    updateProduct,
    deleteProduct,
    toggleProductStock,
    addSpecialOffer,
    updateSpecialOffer,
    deleteSpecialOffer,
    addCoupon,
    updateCoupon,
    deleteCoupon,
    adminCategories,
    loadAdminCategories,
    createCategory,
    updateCategory,
    toggleCategoryStatus,
    deleteCategory,
    adminOrders,
    loadAdminOrders,
    confirmOrder,
    cancelOrder,
    updateOrderStatus,
    adminClients,
    loadAdminClients,
    updateClient,
    deleteClient,
    parsePrice,
    getActiveOffers,
    getProductDiscount,
    calculateDiscountedPrice,
    whatsappNumber,
    openWhatsApp,
    adminUser,
    adminToken,
  ]);

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>;
};

export default AppContext;
